import React, { useCallback, useState, useReducer } from 'react'
import context from '@/context'
import { ModalBody, Form, FormGroup, Label, Row, Col } from 'reactstrap'
import Resx from '@/components/Resx'
import { DropDownList } from '@/components/dropDownList'
import { BasicHeader } from '@/dialogs/common/basicHeader'
import { ValidationBoundary, ValidationState } from '@/components/validation'
import ErrorBoundary from '@/components/ErrorBoundary'
import { ConfirmFooter } from '@/dialogs/common/confirmFooter'
import { EnumDropDownList } from '@/components/enumDropDownList'
import { institutionTypes } from '@/enums/institutionTypes'
import { Radio } from '@/components/radio'
import { useLocalization } from '@/hooks/localization'
import { intMaxValue } from '@/utils/constants'
import { InstitutionPermissionCheckBoxes } from './institutionPermissionCheckBoxes'
import PropTypes from 'prop-types'
import { getDefaultState, reducer, actionTypes } from './reducer'

const InstitutionPermissionDialog = React.memo(function InstitutionPermissionDialog ({ institutionPermission, onAction, ...props }) {
  const [state, dispatch] = useReducer(reducer, getDefaultState(institutionPermission, false))

  const { institutionType, institution, bankType, companyType, ownershipType, individualReadOnly,
    individualEdit, institutionReadOnly, institutionEdit } = state

  const [view, setView] = useState((institutionPermission.institution || {}).id ? 'institution' : 'by-filter')
  const viewChange = useCallback(e => {
    if (e.target.value === 'institution') {
      dispatch({ type: actionTypes.clearStateByFilter })
    }
    if (e.target.value === 'by-filter') {
      dispatch({ type: actionTypes.clearStateInstitution })
    }

    setView(e.target.value)
  }, [])

  const institutionTypeChange = useCallback(e => dispatch({ type: actionTypes.institutionTypeChange, data: e }), [])
  const institutionChange = useCallback((e) => dispatch({ type: actionTypes.institutionChange, data: e.item }), [])
  const bankTypeChange = useCallback((e) => dispatch({ type: actionTypes.bankTypeChange, data: e.item }), [])
  const companyTypeChange = useCallback((e) => dispatch({ type: actionTypes.companyTypeChange, data: e.item }), [])
  const ownershipTypeChange = useCallback((e) => dispatch({ type: actionTypes.ownershipTypeChange, data: e.item }), [])
  const individualReadOnlyChange = useCallback((value) => dispatch({ type: actionTypes.individualReadOnlyChange, data: value }), [])
  const individualEditChange = useCallback((value) => dispatch({ type: actionTypes.individualEditChange, data: value }), [])
  const institutionReadOnlyChange = useCallback((value) => dispatch({ type: actionTypes.institutionReadOnlyChange, data: value }), [])
  const institutionEditChange = useCallback((value) => dispatch({ type: actionTypes.institutionEditChange, data: value }), [])

  const onSave = useCallback(() => onAction({ ...state }), [onAction, state])

  const institutionDataSource = useCallback((req) => context.api.institutionService.getLookup(req), [])
  const { localize } = useLocalization()
  return (
    <ValidationBoundary>
      <BasicHeader title='Permissions.InstitutionPermission' {...props} />
      <ModalBody>
        <ErrorBoundary>
          <Form>
            <Row>
              <Col>
                <FormGroup>
                  <Radio.Group onChange={viewChange} value={view}>
                    <Radio value='by-filter'>{localize('Permissions.InstitutionPermissionByFilter')}</Radio>
                    <Radio value='institution'>{localize('Permissions.InstitutionPermissionByInstitution')}</Radio>
                  </Radio.Group>
                </FormGroup>
                {view === 'institution' &&
                <FormGroup>
                  <Label><Resx name='Permissions.Institution' /></Label>
                  <DropDownList
                    dataSource={institutionDataSource}
                    onChange={institutionChange}
                    value={institution}
                  />
                </FormGroup>}
                {view === 'by-filter' &&
                <React.Fragment>
                  <FormGroup>
                    <Label><Resx name='Permissions.InstitutionType' /></Label>
                    <EnumDropDownList
                      enumDefinition={institutionTypes} allowClear
                      onChange={institutionTypeChange}
                      value={institutionType}
                    />
                  </FormGroup>
                  {institutionType !== institutionTypes.enum.Company && <FormGroup>
                    <Label><Resx name='Permissions.BankType' /></Label>
                    <DropDownList
                      dataSource={context.cache.getBankTypes}
                      onChange={bankTypeChange}
                      value={bankType}
                    />
                  </FormGroup>}
                  {institutionType !== institutionTypes.enum.Bank && <FormGroup>
                    <Label><Resx name='Permissions.CompanyType' /></Label>
                    <DropDownList
                      dataSource={context.cache.getCompanyTypes}
                      onChange={companyTypeChange}
                      value={companyType}
                    />
                  </FormGroup>}
                  <FormGroup>
                    <Label><Resx name='Permissions.OwnershipType' /></Label>
                    <DropDownList
                      dataSource={context.cache.getOwnershipTypes}
                      onChange={ownershipTypeChange}
                      value={ownershipType}
                    />
                  </FormGroup>
                </React.Fragment>}
              </Col>
              <Col>
                <FormGroup>
                  <InstitutionPermissionCheckBoxes
                    individualReadOnly={individualReadOnly || 0}
                    onIndividualReadOnlyChange={individualReadOnlyChange}
                    individualEdit={individualEdit || 0}
                    onIndividualEditChange={individualEditChange}
                    institutionReadOnly={institutionReadOnly || 0}
                    onInstitutionReadOnlyChange={institutionReadOnlyChange}
                    institutionEdit={institutionEdit || 0}
                    onInstitutionEditChange={institutionEditChange} />
                </FormGroup>
              </Col>
            </Row>
          </Form>
        </ErrorBoundary>
      </ModalBody>
      <ValidationState render={({ isValid }) =>
        <ConfirmFooter primaryButtonText={institutionPermission.id ? 'Common.Edit' : 'Common.Add'} icon='save' {...props} onAction={onSave} disabled={!isValid} />
      } />
    </ValidationBoundary>
  )
})

InstitutionPermissionDialog.propTypes = {
  institutionPermission: PropTypes.shape({
    id: PropTypes.number,
    jobPositionAssignmentId: PropTypes.number,
    institutionType: PropTypes.string,
    institution: PropTypes.object,
    bankType: PropTypes.string,
    companyType: PropTypes.string,
    ownershipType: PropTypes.string,
    individualReadOnly: PropTypes.number,
    individualEdit: PropTypes.number,
    institutionReadOnly: PropTypes.number,
    institutionEdit: PropTypes.number,
    isCbiPermission: PropTypes.bool
  }).isRequired,
  onAction: PropTypes.func.isRequired
}

export const maxInt = intMaxValue

export function getInstitutionPermissions (targetId, data) {
  return !targetId ? maxInt : data.getIn(['modifiedData', 'permissions'])
}

export default {
  modalProps: {
    size: 'lg'
  },
  component: InstitutionPermissionDialog
}



// WEBPACK FOOTER //
// ./src/dialogs/institutionPermission/institutionPermission.js